import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { cloneDeep, isEmpty, size, sortBy, isEqual } from 'lodash';
import {
  Application,
  ApplicationsService,
  BenchmarkService,
  DialogService,
  TabulatorService,
  TransformationService,
  ValuesExchangeProvider
} from '@nrwl/common-lib';
import { takeUntil } from 'rxjs/operators';
import { SelectionChange } from '@angular/cdk/collections';
import { ViaviTableComponent } from '@nrwl/viavi-tabulator';

@Component({
  selector: 'nrwl-apps-entity-widget',
  templateUrl: './applications.entity.widget.component.html',
  styleUrls: ['./applications.entity.widget.component.scss']
})
export class ApplicationsEntityWidgetComponent implements OnInit, OnDestroy {
  @Input() data: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @ViewChild(ViaviTableComponent) child: ViaviTableComponent;
  pageSize = 10;
  applicationData: Application[];
  prevApplicationData: Application[];
  filteredData: Application[];
  applicationListData: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  title: string;
  onTableRowClick: (click) => void;
  onTableFiltered: (click) => void;
  applicationDetails: Application;
  dataSourceHeader: [];
  filteredDataItems: string[];
  queryData: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  showPaginator: BehaviorSubject<boolean>;
  outputs: any = {}; //eslint-disable-line @typescript-eslint/no-explicit-any
  deleteApplicationBulkURL = '/entity/v1/application/bulk';
  searchText: string;
  authorized = false;
  tableOptions = {
    hideActionsColumn: false,
    defaultSorting: {
      id: 'name',
      start: 'asc'
    }
  };
  isLoading = true;
  dataSourceSubscription: Subscription;
  selectedApp = null;
  //eslint-disable-line
  table: any;
  private lastRequest: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    readonly applicationsService: ApplicationsService,
    private readonly transformService: TransformationService,
    private readonly dialogService: DialogService,
    private readonly exchange: ValuesExchangeProvider,
    private readonly benchmarkService: BenchmarkService,
    private tabulatorService: TabulatorService
  ) {
    this.onTableRowClick = null;
    this.onTableFiltered = null;
    this.filteredDataItems = [];
    this.showPaginator = new BehaviorSubject<boolean>(true);
    this.queryData = {};
    this.lastRequest = null;
    this.benchmarkService.registerPageEvent('applicationPageRendering');
    this.table = this.tabulatorService.commonTableConfiguration();
    this.table.groupHeader = this.tabulatorService.groupHeader;
    this.table.initialSort = [{ column: 'name', dir: 'desc' }];
  }

  ngOnInit(): void {
    this.exchange.notifyListeners('new_app', { noValue: true });
    this.exchange.registerInterest('new_app', app => this.updateCache(app));
    this.applicationsService.clearQueues();
    this.title = 'Your applications';
    this.authorized = this.applicationsService.authorized;
    this.tableOptions.hideActionsColumn = !this.authorized;

    this.onTableRowClick = data => {
      if (!isEmpty(data)) {
        this.selectedApp = data;
        this.applicationDetails = this.getAppById(data.id);
      }
    };
    this.onTableFiltered = filter => {
      if (filter) {
        const data = this.child.filteredItems();
        if (!isEmpty(data)) {
          this.selectedApp = data[0];
          this.applicationDetails = this.getAppById(data[0]?.id);
        }
      }
    };
    this.dataSourceSubscription = this.data.subscribe(data => {
      if (data && data.noValue) {
        this.benchmarkService.registerPageEvent('applicationPageRendering');
        // ignore - this is a dummy value, indicating we still dont know whether any apps data exist
        return;
      }

      this.isLoading = false;
      this.assignData(data);
      this.filteredData = data;
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  updateCache(data: any): void {
    if (!data.noValue) {
      this.applicationsService.addApplicationResponse();
    }
  }

  get isActionsDisabled(): boolean {
    return this.getSelectedItemsIds().length === 0;
  }

  assignData(data: Array<Application>): void {
    this.applicationData = sortBy(data, app => app.getName().toLowerCase());
    this.applicationListData = this.transformService.transformData(
      'application-management',
      this.applicationData
    );
    const minimalAppData = this.applicationData.map(data => {
      return {
        name: data.getName(),
        url: data.getUrl(),
        id: data.getId()
      };
    });
    if (isEqual(minimalAppData, this.prevApplicationData)) {
      // this.log.debug('Application Entity Widget data has not changed');
      return;
    }
    if (this.selectedApp && this.applicationData.some(app => app.getId() === this.selectedApp.id)) {
      this.onTableRowClick(this.selectedApp);
    } else {
      this.onTableRowClick(this.applicationListData.data[0]);
    }
    this.table.columns = this.applicationListData.column;
    this.table.data = this.applicationListData.data;
    this.table = Object.assign({}, this.table);
    this.prevApplicationData = cloneDeep(minimalAppData);
    this.benchmarkService.logPageEventTime('applicationpage rendering', 'applicationPageRendering');
  }

  getAppById(id: string): Application {
    return this.applicationData.find(app => app.getId() === id);
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  sendAction(params: { method: string; action: string; data: any }): void {
    const items = params.data || this.getSelectedItemsIds(),
      url = this.deleteApplicationBulkURL;
    const itemsLength = size(items);
    if (itemsLength > 0 && this.deleteApplicationBulkURL) {
      const labelId = itemsLength > 1 ? 'applications.delete' : 'application.delete';
      const newParams = {
        id: labelId,
        callBackService: this,
        callBackFnParams: {
          url: url,
          appIds: items
        },
        callBackFuntion: this.callBackFunction
      };
      this.dialogService.showDialog('confirm', 'body', newParams);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  callBackFunction(service: any, params: any): void {
    service._removeApplication$(service, params);
  }

  deleteApplicationEvent(appId: string): void {
    const params = {};
    params['appIds'] = [appId];
    params['url'] = this.deleteApplicationBulkURL;
    this._removeApplication$(this, params);
  }

  addApplication(): void {
    this.applicationsService.navigateToWizard('new_application');
  }
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onSelectionsChanged(event: SelectionChange<any>): void {
    event.source.selected;
  }

  private getSelectedItemsIds(): string[] {
    return this.child?.selection.map(x => x.id) || [];
  }

  private _removeApplication$(service, params): void {
    this.isLoading = true;
    service.applicationsService
      .removeApplication$(params.url, params.appIds)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.searchText = '';
          service.applicationsService.removeApplicationResponse(response);
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
        },
        () => {}
      );
  }
}
